import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, UserPlus, Check, X, Trash2, Users as UsersIcon, Mail, Phone, MessageCircle } from 'lucide-react';
import { Card, Button, useToast } from '@caruso/ui';
import { eventsService } from '@/services/events.service';
import whatsappService from '@/services/whatsapp.service';

export function EventGuestsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: event, isLoading } = useQuery({
    queryKey: ['event', id],
    queryFn: () => eventsService.getById(id!),
    enabled: !!id,
  });

  const updateGuestMutation = useMutation({
    mutationFn: ({ guestId, confirmed, attended }: { guestId: string; confirmed?: boolean; attended?: boolean }) =>
      eventsService.updateGuest(id!, guestId, { confirmed, attended }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', id] });
      toast({
        title: 'Atualizado!',
        description: 'Status do convidado atualizado',
        variant: 'success',
      });
    },
  });

  const removeGuestMutation = useMutation({
    mutationFn: (guestId: string) => eventsService.removeGuest(id!, guestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', id] });
      toast({
        title: 'Removido!',
        description: 'Convidado removido do evento',
        variant: 'success',
      });
    },
  });

  const handleSendWhatsAppInvite = async (guestId: string) => {
    try {
      const invitationData = await eventsService.getInvitationLink(id!, guestId);

      // Format phone number (remove non-digits)
      const phone = invitationData.guest.phone?.replace(/\D/g, '');

      if (!phone) {
        toast({
          title: 'Erro!',
          description: 'Convidado não possui telefone cadastrado',
          variant: 'destructive',
        });
        return;
      }

      // Format event date
      const eventDate = new Date(invitationData.event.eventDate).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

      // Create WhatsApp message
      const message = `Olá ${invitationData.guest.name}! 👋

Você está convidado(a) para o evento *${invitationData.event.name}*!

📅 Data: ${eventDate}
${invitationData.event.location ? `📍 Local: ${invitationData.event.location}\n` : ''}
Por favor, confirme sua presença através do link abaixo:

✅ Confirmar presença: ${invitationData.confirmUrl}

❌ Não poderei comparecer: ${invitationData.declineUrl}

Aguardamos você!`;

      // Send WhatsApp message using internal service
      await whatsappService.sendText({
        number: phone,
        text: message,
        linkPreview: true,
      });

      toast({
        title: 'Sucesso!',
        description: 'Convite enviado via WhatsApp',
        variant: 'success',
      });
    } catch (error) {
      toast({
        title: 'Erro!',
        description: 'Não foi possível enviar o convite via WhatsApp',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return <div className="text-white">Carregando...</div>;
  }

  if (!event) {
    return <div className="text-white">Evento não encontrado</div>;
  }

  const confirmedGuests = event.guests?.filter(g => g.confirmed) || [];
  const pendingGuests = event.guests?.filter(g => !g.confirmed) || [];
  const attendedGuests = event.guests?.filter(g => g.attended) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/app/eventos')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-4xl font-bold text-white">{event.name}</h1>
          <p className="text-brand-ivory/80 mt-1">Gerenciar Convidados</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-6 bg-brand-smoke border-brand-gold/20">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-brand-gold/10 rounded-lg">
              <UsersIcon className="w-6 h-6 text-brand-gold" />
            </div>
            <div>
              <p className="text-brand-ivory/70 text-sm">Total</p>
              <p className="text-3xl font-bold text-white">{event.guests?.length || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-brand-smoke border-brand-gold/20">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-500/10 rounded-lg">
              <Check className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-brand-ivory/70 text-sm">Confirmados</p>
              <p className="text-3xl font-bold text-white">{confirmedGuests.length}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-brand-smoke border-brand-gold/20">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-yellow-500/10 rounded-lg">
              <X className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <p className="text-brand-ivory/70 text-sm">Pendentes</p>
              <p className="text-3xl font-bold text-white">{pendingGuests.length}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-brand-smoke border-brand-gold/20">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-500/10 rounded-lg">
              <UsersIcon className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-brand-ivory/70 text-sm">Compareceram</p>
              <p className="text-3xl font-bold text-white">{attendedGuests.length}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Guests List */}
      <Card className="p-6 bg-brand-smoke border-brand-gold/20">
        <h2 className="text-2xl font-bold text-white mb-6">Lista de Convidados</h2>

        {event.guests && event.guests.length > 0 ? (
          <div className="space-y-3">
            {event.guests.map((guest) => (
              <div
                key={guest.id}
                className="flex items-center justify-between p-4 bg-brand-ink/30 rounded-lg border border-brand-gold/10 hover:bg-brand-ink/50 transition-colors"
              >
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white">{guest.client?.name}</h3>
                  <div className="flex gap-4 mt-2">
                    {guest.client?.email && (
                      <div className="flex items-center gap-2 text-sm text-brand-ivory/70">
                        <Mail className="w-4 h-4" />
                        <span>{guest.client.email}</span>
                      </div>
                    )}
                    {guest.client?.phone && (
                      <div className="flex items-center gap-2 text-sm text-brand-ivory/70">
                        <Phone className="w-4 h-4" />
                        <span>{guest.client.phone}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSendWhatsAppInvite(guest.id)}
                      className="text-green-400 border-green-400/30 hover:bg-green-400/10"
                    >
                      <MessageCircle className="w-4 h-4 mr-1" />
                      Enviar Convite
                    </Button>

                    <Button
                      variant={guest.confirmed ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => updateGuestMutation.mutate({ guestId: guest.id, confirmed: !guest.confirmed })}
                      disabled={updateGuestMutation.isPending}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      {guest.confirmed ? 'Confirmado' : 'Confirmar'}
                    </Button>

                    <Button
                      variant={guest.attended ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => updateGuestMutation.mutate({ guestId: guest.id, attended: !guest.attended })}
                      disabled={updateGuestMutation.isPending}
                    >
                      <UsersIcon className="w-4 h-4 mr-1" />
                      {guest.attended ? 'Compareceu' : 'Marcar Presença'}
                    </Button>
                  </div>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeGuestMutation.mutate(guest.id)}
                    disabled={removeGuestMutation.isPending}
                    className="text-red-400 hover:bg-red-400/10"
                  >
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <UsersIcon className="w-16 h-16 text-brand-gold/50 mx-auto mb-4" />
            <p className="text-brand-ivory/60">Nenhum convidado adicionado ainda</p>
          </div>
        )}
      </Card>
    </div>
  );
}
